/**
 * useAvatar Custom Hook
 * IDENTITY:P2.D — Avatar State Management
 *
 * Manages avatar state (current, options, history, loading, error).
 * Auto-fetches current avatar on mount.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  generateAvatar,
  selectAvatar,
  getCurrentAvatar,
  getAvatarHistory,
  removeAvatar,
} from '../services/avatarApi.js';

export function useAvatar() {
  const [currentAvatar, setCurrentAvatar] = useState(null);
  const [fallback, setFallback] = useState(null);
  const [avatarOptions, setAvatarOptions] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getCurrentAvatar();
      setCurrentAvatar(data.avatar);
      setFallback(data.fallback);
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Failed to load avatar');
      setCurrentAvatar(null);
      setFallback(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const generate = useCallback(async (stylePreset, customPrompt, paymentIntentId) => {
    try {
      setIsGenerating(true);
      setError(null);
      const data = await generateAvatar(stylePreset, customPrompt, paymentIntentId);
      setAvatarOptions(data.options || []);
      return data;
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Avatar generation failed');
      throw err;
    /* c8 ignore next 2 */
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const select = useCallback(async (avatarId) => {
    try {
      setIsSelecting(true);
      setError(null);
      const data = await selectAvatar(avatarId);
      setCurrentAvatar(data);
      setAvatarOptions([]);
      return data;
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Avatar selection failed');
      throw err;
    /* c8 ignore next 2 */
    } finally {
      setIsSelecting(false);
    }
  }, []);

  const remove = useCallback(async () => {
    try {
      setError(null);
      await removeAvatar();
      setCurrentAvatar(null);
      await refresh();
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Failed to remove avatar');
      throw err;
    }
  }, [refresh]);

  const loadHistory = useCallback(async (page = 1, limit = 20) => {
    try {
      const data = await getAvatarHistory(page, limit);
      setHistory(data.avatars || []);
      setHistoryTotal(data.total || 0);
      return data;
    } catch (err) {
      /* c8 ignore next */
      setError(err.message || 'Failed to load history');
      throw err;
    }
  }, []);

  return {
    currentAvatar,
    fallback,
    avatarOptions,
    history,
    historyTotal,
    isGenerating,
    isSelecting,
    isLoading,
    error,
    generate,
    select,
    remove,
    refresh,
    loadHistory,
  };
}
